// Settings.jsx
import BaseAppLayout from "../../layouts/BaseAppLayout/BaseAppLayout";
import { useAuth } from "../../store/AuthContext/AuthContext";
import Typography from "../../components/Typography/Typography";
import { Cog, Settings2, UserRound } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import ApiManager from "../../api/ApiManager";
import Paywall from "../../components/Paywall/Paywall";
import MenuItem from "./MenuItem";
import ProfileSection from "./ProfileSection";
import PreferencesSection from "./PreferencesSection";
import OtherSection from "./OtherSection";

export default function Settings() {
  const { 
    logout, 
    getCookie, 
    checkSubscription, 
    isSubbed, 
    userCreatedAt,
    subscriptionStartDate,
    subscriptionEndDate 
  } = useAuth();
  
  const [activeMenuItem, setActiveMenuItem] = useState("profilo");
  const [activeIndicatorStyle, setActiveIndicatorStyle] = useState({ left: 0, width: 0 });
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPaywallOpened, setIsPaywallOpened] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isSubbed: isSubbed,
    userCreatedAt: userCreatedAt,
    subscriptionStartDate: subscriptionStartDate,
    subscriptionEndDate: subscriptionEndDate
  });

  // Create unique references for menu items
  const menuRefs = useRef({
    profilo: null,
    preferenze: null,
    altro: null,
  });

  // Function to fetch user details
  const fetchUserDetails = useCallback(() => {
    const api = new ApiManager();
    const id_token = getCookie("id_token");

    api.get("/user/retrieve", {
      headers: {
        Authorization: `Bearer ${id_token}`,
      }
    })
      .then((response) => {
        if (response.data && response.data.user_data) {
          const user = response.data.user_data;
          console.log("User details:", user);
          setUserData(user);
        } else {
          console.error("User details are missing or malformed.");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching user details:", error);
        setLoading(false);
      });
  }, [getCookie]);

  // Verifica lo stato dell'abbonamento quando il componente viene montato
  useEffect(() => {
    const verifySubscription = async () => {
      try {
        const status = await checkSubscription();
        setSubscriptionStatus(status);
        console.log("Subscription status:", status);
      } catch (error) {
        console.error("Error checking subscription:", error);
      }
    };

    verifySubscription();
  }, [checkSubscription]);

  useEffect(() => {
    // Fetch user details when component mounts
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    const activeElement = menuRefs.current[activeMenuItem];
    if (activeElement) {
      setActiveIndicatorStyle({
        left: activeElement.offsetLeft,
        width: activeElement.offsetWidth,
      });
    }
  }, [activeMenuItem]);

  const handleFieldUpdate = (field, value) => {
    const updatedData = { ...userData, [field]: value };
    setUserData(updatedData);

    const api = new ApiManager();

    api.post("/onboarding", {
      discoveryChannel: updatedData.DiscoveryChannel,
      dueRemainingTime: updatedData.DueRemainingTime,
      extraInfo: updatedData.ExtraInfo,
      extraInfoText: updatedData.ExtraInfoText,
      studyField: updatedData.StudyField,
      thesisPrompt: updatedData.ThesisPrompt,
      thesisType: updatedData.ThesisType
    },
    {
      headers: {
        Authorization: `Bearer ${getCookie("id_token")}`,
      }
    }).then((response) => {
      if (response.data && response.data.statusCode === 200) {
        console.log("User data updated successfully:", response.data);
      } else {
        console.error("Error updating user data:", response.data);
      }
    }).catch((error) => {
      console.error("Error updating user data:", error);
    });
  };

  const renderContent = () => {
    switch (activeMenuItem) {
      case "profilo":
        return (
          <ProfileSection 
            loading={loading} 
            userData={userData} 
            userSubData={subscriptionStatus}
            logout={logout} 
            handleUpgradeClick={() => setIsPaywallOpened(true)} 
          />
        );
      case "preferenze":
        return <PreferencesSection userData={userData} />;
      case "altro":
        return <OtherSection userData={userData} handleFieldUpdate={handleFieldUpdate} />;
      default:
        return null;
    }
  };

  return (
    <BaseAppLayout headerText="Impostazioni">
      <div className="w-full flex flex-col px-6 md:px-[120px] gap-6 pt-10 pb-20">
        <div className="flex flex-col gap-9">
          <div className="flex flex-col gap-12">
            <Typography size="48" family="serif" weight="sb">Impostazioni</Typography>
            <div className="relative flex flex-col gap-4">
              <div className="flex gap-8">
                {["profilo", "preferenze", "altro"].map((item) => (
                  <MenuItem
                    key={item}
                    icon={item === "profilo" ? UserRound : item === "preferenze" ? Settings2 : Cog}
                    label={item.charAt(0).toUpperCase() + item.slice(1)}
                    isActive={activeMenuItem === item}
                    onClick={() => setActiveMenuItem(item)}
                    itemRef={(el) => (menuRefs.current[item] = el)}
                  />
                ))}
              </div>
              <div
                className="absolute bottom-0 h-[4px] bg-accent transition-all duration-300"
                style={{ left: activeIndicatorStyle.left, width: activeIndicatorStyle.width }}
              />
              <div className="w-full border-[1px] border-gray-150" />
            </div>
          </div>
          {renderContent()}
        </div>
      </div>
      <Paywall
        open={isPaywallOpened}
        onClose={() => setIsPaywallOpened(false)}
      />
    </BaseAppLayout>
  );
}